import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

export interface OrderFilters {
  page?: number;
  limit?: number;
  startDate?: string;
  endDate?: string;
  updatedStart?: string;
  updatedEnd?: string;
  paymentStatus?: string;
  status?: string;
  employee?: string;
  company?: string;
  plateNumber?: string;
  service?: string;
  addOns?: string;
  sortBy?: string;
  sortOrder?: string;
}

export function useOrderFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse current filters from URL
  const filters = useMemo<OrderFilters>(() => {
    const params: Record<string, string | number> = {};
    
    // Get all URL parameters
    for (const [key, value] of searchParams.entries()) {
      if (key === "page" || key === "limit") {
        params[key] = parseInt(value, 10);
      } else {
        params[key] = value;
      }
    }

    // Convert to OrderFilters type
    const orderFilters: OrderFilters = {
      page: params.page as number || 1,
      limit: params.limit as number || 10,
      startDate: params.startDate as string,
      endDate: params.endDate as string,
      updatedStart: params.updatedStart as string,
      updatedEnd: params.updatedEnd as string,
      paymentStatus: params.paymentStatus as string,
      status: params.status as string,
      employee: params.employee as string,
      company: params.company as string,
      plateNumber: params.plateNumber as string,
      service: params.service as string,
      addOns: params.addOns as string,
      sortBy: (params.sortBy as string) || "createdAt",
      sortOrder: (params.sortOrder as string) || "desc",
    };

    return orderFilters;
  }, [searchParams]);

  // Update filters and URL
  const updateFilters = useCallback(
    (newFilters: Partial<OrderFilters>) => {
      const updatedParams = new URLSearchParams();

      // Merge current filters with new ones
      const mergedFilters = { ...filters, ...newFilters };

      // Add non-empty values to URL params
      Object.entries(mergedFilters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          updatedParams.set(key, String(value));
        }
      });

      setSearchParams(updatedParams);
    },
    [filters, setSearchParams]
  );

  // Clear all filters except defaults
  const clearFilters = useCallback(() => {
    const defaultParams = new URLSearchParams({
      page: "1",
      limit: "10",
      sortBy: "createdAt",
      sortOrder: "desc",
    });
    setSearchParams(defaultParams);
  }, [setSearchParams]);

  // Update specific filter
  const updateFilter = useCallback(
    (key: keyof OrderFilters, value: string | number | undefined) => {
      updateFilters({ [key]: value });
    },
    [updateFilters]
  );

  // Get query string for API calls
  const getQueryString = useCallback(() => {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.set(key, value.toString());
      }
    });

    return params.toString();
  }, [filters]);

  // Convert Date objects to string format for API
  const getApiFilters = useCallback(() => {
    const apiFilters: Record<string, string | number> = {};
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        apiFilters[key] = value;
      }
    });

    return apiFilters;
  }, [filters]);

  return {
    filters,
    updateFilters,
    updateFilter,
    clearFilters,
    getQueryString,
    getApiFilters,
  };
}